import * as React from 'react';
import type { ConnectedProps } from 'react-redux';
import { connect } from 'react-redux';
import { Outlet, useLocation, useNavigate, useParams } from 'react-router-dom-v5-compat';
import { compose } from 'redux';
import type { FormErrors, FormErrorsWithArray, InjectedFormProps } from 'redux-form';
import { reduxForm, getFormValues, formValueSelector, getFormSubmitErrors, getFormSyncErrors } from 'redux-form';

import { isAllowed } from '@acceligentllc/shared/utils/auth';
import { isValidTextInput } from '@acceligentllc/shared/utils/text';
import * as TimeUtils from '@acceligentllc/shared/utils/time';

import { DirectoryIcon } from '@acceligentllc/shared/enums/directoryIcon';
import { Priority } from '@acceligentllc/shared/enums/priority';
import { TimeFormat } from '@acceligentllc/shared/enums/timeFormat';

import { EMAIL_REGEX } from '@acceligentllc/shared/constants/regex';

import type { RootState } from 'af-reducers';

import { useToggle } from 'af-utils/react.util';

import DirectoryNames from 'ab-enums/directoryNames.enum';
import PagePermissions from 'ab-enums/pagePermissions.enum';

import * as FORMS from 'af-constants/reduxForms';
import CLIENT from 'af-constants/routes/client';

import Breadcrumbs from 'af-components/Breadcrumbs';
import ConfirmationModal from 'af-components/ConfirmationModal';
import type { SubjobFM } from 'af-components/SharedForms/Job/formModel';
import JobUpsertFM from 'af-components/SharedForms/Job/formModel';
import SubmitButton from 'af-components/SubmitButton';
import TabNavigation from 'af-components/TabNavigation';

import * as AttachmentActions from 'af-actions/attachment';
import * as JobActions from 'af-actions/jobs';
import * as WorkRequestActions from 'af-actions/workRequests';

import { TABS } from '../helpers';
import styles from '../styles.module.scss';
import type { FormJobOutletContext } from '../types';
import { getRedirectHandler } from './redirectHandler';
import { JobCreateTabRoutes } from './types';

type FormOwnProps = ConnectedProps<typeof connector>;

type Props = FormOwnProps & InjectedFormProps<JobUpsertFM, FormOwnProps>;

const _isInvalidBillingCode = (billingCode) => {
	return Object.keys(billingCode).length > 0;
};

const _initializeRootDirectories = () => {
	return Object.keys(DirectoryNames).map((_name, _index) => {
		const dirName = DirectoryNames[_name];
		const iconEnum = DirectoryIcon[_name];

		return {
			id: _index + 1,
			name: dirName,
			parentId: null,
			icon: iconEnum ?? null,
			lastModifiedAt: null,
			lastModifiedBy: null,
			attachmentIds: [],
		};
	});
};

export interface RedirectParams {
	originUrlForRedirect: string;
	linkForRedirect: string;
	previousFormStateForRedirect: JobUpsertFM;
	defaultDueDateForRedirect: Nullable<Date>;
}

const CreateJob: React.FC<Props> = (props) => {
	const {
		createJob,
		companyName,
		change: _change,
		formJobCode,
		title,
		customerCompanyName,
		priority,
		office,
		handleSubmit,
		jobHazardAssessmentStatus,
		submitting,
		isProject,
		subjobs,
		projectId,
		currentFormValues,
		billToEmail,
		jobCodeSubmitError,
		findAllAvailableSubjobs,
		hasPermissionsToManageBillingCodes,
		initialized,
		syncErrors,
		billingCodesSubmitErrors = [],
		invoiceReminderRecipients = [],
		reset: _reset,
		initialize: _initialize,
		workLocation,
		deletePendingAttachments,
		uploadedAttachmentIds,
	} = props;

	const location = useLocation();
	const { state, pathname } = location;
	const { orgAlias, originUrl, defaultDueDate, jobCode, previousFormState, subjob, redirectUrl } = state;
	const { '*': tabId } = useParams();
	const navigate = useNavigate();

	const dueDate = React.useMemo(() =>
		defaultDueDate ?? TimeUtils.formatDate(defaultDueDate, TimeFormat.DB_DATE_ONLY, TimeFormat.JS_TIME),
		[defaultDueDate]);

	const change = React.useMemo(() => _change
		// eslint-disable-next-line react-hooks/exhaustive-deps
		, []);

	const reset = React.useMemo(() => _reset
		// eslint-disable-next-line react-hooks/exhaustive-deps
		, []);

	const initialize = React.useMemo(() => _initialize
		// eslint-disable-next-line react-hooks/exhaustive-deps
		, []);

	const {
		value: scrollToTopOfFormForSubjobRedirect,
		setToTrue: activateScrollToTopOfFormForSubjobRedirect,
		setToFalse: deactivateScrollToTopOfFormForSubjobRedirect,
	} = useToggle(false);

	const redirectParamsRef = React.useRef<Nullable<RedirectParams>>(null);
	const uploadedAttachmentIdsToDeleteRef = React.useRef<Nullable<number[]>>(null);

	// every time path name is the default path route (index!) for this component, we should update state using info from location
	// this means that we navigated to the create job form without reloading - from create new subjob on another create job form
	// (path: /company/PewterschmidtIndustries/jobs/create)
	const redirectParams = React.useMemo(() => {
		if (pathname === CLIENT.COMPANY.JOBS.CREATE(orgAlias, companyName)) {

			// if we are creating job coming back from subjob create
			if (previousFormState && !redirectUrl) {
				reset();

				// Directly handle the special case for 'subjobs'
				if (previousFormState.hasOwnProperty('subjobs') || previousFormState.isProject) {
					const updatedSubjobs = subjob ? [...(previousFormState.subjobs ?? []), subjob] : (previousFormState.subjobs ?? []);
					change('subjobs', updatedSubjobs);
				}

				// Iterate over and update the rest of the form state
				Object.entries(previousFormState).forEach(([key, value]) => {
					if (key !== 'subjobs') { // Skip 'subjobs' since it's already handled
						change(key, value);
					}
				});
			} else {
				// creating subjob coming from new job form
				initialize({
					priority: Priority.MEDIUM,
					estimateTotal: 0,
					numberOfEmployees: 0,
					directories: _initializeRootDirectories(),
				});
				if (jobCode) {
					change('jobCode', jobCode);
				}
			}

			const newRedirectParams = {
				originUrlForRedirect: originUrl ?? '',
				linkForRedirect: redirectUrl ?? '',
				previousFormStateForRedirect: previousFormState ?? null,
				defaultDueDateForRedirect: defaultDueDate ?? null,
			};

			redirectParamsRef.current = newRedirectParams;
			return newRedirectParams;
		} else {
			return redirectParamsRef.current;
		}
	}, [change,
		companyName,
		defaultDueDate,
		initialize,
		jobCode,
		orgAlias,
		originUrl,
		pathname,
		previousFormState,
		redirectUrl,
		reset,
		subjob,
		redirectParamsRef,
	]);

	React.useEffect(() => {
		if (pathname === CLIENT.COMPANY.JOBS.CREATE(orgAlias, companyName)) {
			// scroll to top of job create form when landing on it for the first time
			// necessary for case when you are creating new subjob from creating new project
			// because then we just reinitialize the form values, and scroll to the top to make it look like it is new component
			deactivateScrollToTopOfFormForSubjobRedirect();
		}
	}, [companyName, deactivateScrollToTopOfFormForSubjobRedirect, orgAlias, pathname]);

	const clearPendingAttachments = React.useCallback(async () => {
		if (uploadedAttachmentIdsToDeleteRef.current?.length) {
			await deletePendingAttachments({ attachmentIds: uploadedAttachmentIdsToDeleteRef.current });
		}
	}, [deletePendingAttachments]);

	React.useEffect(() => {
		return () => {
			clearPendingAttachments();
		};
	}, [clearPendingAttachments]);

	React.useEffect(() => {
		uploadedAttachmentIdsToDeleteRef.current = uploadedAttachmentIds ?? null;
	}, [uploadedAttachmentIds]);

	const [availableSubjobs, setAvailableSubjobs] = React.useState<SubjobFM[]>([]);
	const [selectedSubjob, setSelectedSubjob] = React.useState<Nullable<SubjobFM>>(null);
	const [subjobSearchText, setSubjobSearchText] = React.useState<string>('');

	const activeTabId = React.useMemo(() => {
		switch (tabId) {
			case JobCreateTabRoutes.DETAILS:
				return TABS[0].id;
			case JobCreateTabRoutes.JOB_HAZARD_ASSESSMENT:
				return TABS[1].id;
			case JobCreateTabRoutes.BILLING_CODES:
				return TABS[2].id;
			case JobCreateTabRoutes.ATTACHMENTS:
				return TABS[3].id;
			default:
				return TABS[0].id;
		}
	}, [tabId]);

	const {
		value: showTurnToProjectConfirmationModal,
		setToTrue: openTurnToProjectConfirmationModal,
		setToFalse: hideTurnToProjectConfirmationModal,
	} = useToggle(false);

	const {
		value: showAddSubjobConfirmationModal,
		setToTrue: openAddSubjobConfirmationModal,
		setToFalse: hideAddSubjobConfirmationModal,
	} = useToggle(false);

	const {
		value: showSubmitWithSubjobsConfirmationModal,
		setToTrue: openSubmitWithSubjobsConfirmationModal,
		setToFalse: hideSubmitWithSubjobsConfirmationModal,
	} = useToggle(false);

	const {
		value: hasAddedSujobs,
		setToTrue: setAddedSujobs,
	} = useToggle(false);

	const {
		value: turnToProjectDisabled,
		setToTrue: disableTurnToProject,
	} = useToggle(isProject);

	React.useEffect(() => {
		if (jobCode) {
			change('jobCode', jobCode);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const closeTurnToProjectConfirmationModal = React.useCallback(async () => {
		change('isProject', false);
		hideTurnToProjectConfirmationModal();
	}, [change, hideTurnToProjectConfirmationModal]);

	const closeAddSubjobConfirmationModal = React.useCallback(async () => {
		hideAddSubjobConfirmationModal();
	}, [hideAddSubjobConfirmationModal]);

	const addSubjob = React.useCallback(() => {
		setAddedSujobs();
		if (selectedSubjob) {
			change('subjobs', [...(subjobs ?? []), selectedSubjob]);
			setAvailableSubjobs([...availableSubjobs.filter((_sj) => _sj.id !== selectedSubjob.id)]);
			setSelectedSubjob(null);
		}
	}, [availableSubjobs, change, selectedSubjob, setAddedSujobs, subjobs]);

	const goToJobForm = React.useCallback(() => {
		// redirects user to job form. when user fills the form and save it, he/she will be
		// redirected back to original job form with new subjob and all other changes
		activateScrollToTopOfFormForSubjobRedirect();

		navigate(CLIENT.COMPANY.JOBS.CREATE(orgAlias, companyName), {
			state: {
				redirectUrl: CLIENT.COMPANY.JOBS.CREATE(orgAlias, companyName),
				defaultDueDate: dueDate ? TimeUtils.parseDate(dueDate) : null,
				orgAlias,
				jobCode: subjobSearchText,
				previousFormState: currentFormValues,
			},
		});
	}, [companyName, currentFormValues, dueDate, navigate, orgAlias, subjobSearchText, activateScrollToTopOfFormForSubjobRedirect]);

	const handleSubjobSelect = React.useCallback((selectedOption: Nullable<SubjobFM>) => {
		setSelectedSubjob(selectedOption);
	}, []);

	const onTurnToProjectChange = React.useCallback((value: boolean) => {
		if (value) {
			openTurnToProjectConfirmationModal();
		}
	}, [openTurnToProjectConfirmationModal]);

	const onSubmit = React.useCallback(async (form: JobUpsertFM) => {
		const job = await createJob(JobUpsertFM.toCreateRequestModel(form));
		if (!job) {
			throw new Error('Job not defined');
		}

		const linkForRedirect = redirectParams?.linkForRedirect;

		const redirectHandler = getRedirectHandler(linkForRedirect);
		if (redirectHandler && redirectParams) {
			redirectHandler(
				job,
				orgAlias,
				{
					...redirectParams,
					navigate,
					activateScrollToTopOfFormForSubjobRedirect,
				}
			);
			return;
		} else if (redirectParams?.originUrlForRedirect) {
			navigate(redirectParams.originUrlForRedirect);
		} else {
			navigate(CLIENT.COMPANY.JOBS.PREVIEW(orgAlias, companyName, `${job.id}`));
		}
	}, [createJob, redirectParams, navigate, orgAlias, companyName, activateScrollToTopOfFormForSubjobRedirect]);

	const onCreateJob = React.useCallback(async (form: JobUpsertFM) => {
		await onSubmit(form);
	}, [onSubmit]);

	const beforeCreateJob = React.useCallback(async (form: JobUpsertFM) => {
		if (!hasAddedSujobs) {
			await onCreateJob(form);
			return;
		} else {
			openSubmitWithSubjobsConfirmationModal();
		}
	}, [hasAddedSujobs, onCreateJob, openSubmitWithSubjobsConfirmationModal]);

	const onTabSelect = React.useCallback((newTabId: number) => {
		let tabName = '';
		switch (newTabId) {
			case TABS[0].id:
				tabName = JobCreateTabRoutes.DETAILS;
				break;
			case TABS[1].id:
				tabName = JobCreateTabRoutes.JOB_HAZARD_ASSESSMENT;
				break;
			case TABS[2].id:
				tabName = JobCreateTabRoutes.BILLING_CODES;
				break;
			case TABS[3].id:
				tabName = JobCreateTabRoutes.ATTACHMENTS;
				break;
			default:
				tabName = JobCreateTabRoutes.DETAILS;
				break;
		}

		navigate(CLIENT.COMPANY.JOBS.CREATE_TAB(orgAlias, companyName, tabName));
	}, [companyName, navigate, orgAlias]);

	const outletContext = React.useMemo<FormJobOutletContext>(() => ({
		availableSubjobs: availableSubjobs,
		change,
		disableTurnToProject,
		formName: FORMS.JOB_CREATE,
		goToJobForm,
		handleSubjobSelect,
		isProject,
		onTurnToProjectChange: onTurnToProjectChange,
		openAddSubjobConfirmationModal,
		orgAlias: orgAlias,
		projectId: projectId,
		redirectOnSubmit: !!redirectParams?.linkForRedirect,
		selectedSubjob,
		setSubjobSearchText,
		subjobs,
		subjobSearchText,
		turnToProjectDisabled,
		initialized,
		billingCodesSyncErrors: syncErrors.billingCodes ?? [],
		billingCodesSubmitErrors,
		scrollToTopOfFormForSubjobRedirect,
		workLocation,
		invoiceReminderRecipients,
	}), [
		availableSubjobs,
		change,
		disableTurnToProject,
		goToJobForm,
		handleSubjobSelect,
		isProject,
		onTurnToProjectChange,
		openAddSubjobConfirmationModal,
		orgAlias,
		projectId,
		redirectParams?.linkForRedirect,
		selectedSubjob,
		subjobs,
		subjobSearchText,
		turnToProjectDisabled,
		initialized,
		syncErrors.billingCodes,
		billingCodesSubmitErrors,
		scrollToTopOfFormForSubjobRedirect,
		workLocation,
		invoiceReminderRecipients,
	]);

	const isBillToEmailValid = billToEmail ? EMAIL_REGEX.test(billToEmail) : true;
	const isWorkRequestTabValid = React.useMemo(() => {
		return (
			!!formJobCode
			&& !!title
			&& !!customerCompanyName
			&& !!priority
			&& !!office
			&& isBillToEmailValid
			&& !jobCodeSubmitError
		);
	}, [formJobCode, title, customerCompanyName, priority, office, isBillToEmailValid, jobCodeSubmitError]);

	const areBillingCodesValid = React.useMemo(() => !syncErrors.billingCodes?.some(_isInvalidBillingCode), [syncErrors.billingCodes]);

	const renderTabLabel = React.useCallback((id: number) => {
		const areRequiredHazardAssessmentFieldsFilled = !!jobHazardAssessmentStatus;
		switch (id) {
			case TABS[0].id: {
				return (
					<div className={styles['job-form__tab']}>
						{!isWorkRequestTabValid && <span className="icon-warning" />}
						Job
					</div>
				) as React.JSX.Element;
			}
			case TABS[1].id: {
				return (
					<div className={styles['job-form__tab']}>
						{!areRequiredHazardAssessmentFieldsFilled && <span className="icon-warning" />}
						Job Hazard Assessment
					</div>
				) as React.JSX.Element;
			}
			case TABS[2].id: {
				return (
					<div className={styles['job-form__tab']}>
						{!areBillingCodesValid && <span className="icon-warning" />}
						Billing Codes
					</div>
				) as React.JSX.Element;
			}
			case TABS[3].id: {
				return (
					<div className={styles['job-form__tab']}>
						Attachments
					</div>
				) as React.JSX.Element;
			}

			default: {
				return <></>;
			}
		}
	}, [jobHazardAssessmentStatus, isWorkRequestTabValid, areBillingCodesValid]);

	const isFormValid = (isBillToEmailValid
		&& isValidTextInput(formJobCode)
		&& isValidTextInput(title)
		&& isValidTextInput(customerCompanyName)
		&& !!priority
		&& !!office
		&& !!jobHazardAssessmentStatus
		&& !syncErrors.billingCodes?.some(_isInvalidBillingCode));

	const fetchAvailableSubjobs = React.useCallback(async () => {
		const _subjobs = await findAllAvailableSubjobs();
		const subjobIds: number[] = subjobs?.map((_s) => _s.id) ?? [];
		setAvailableSubjobs(_subjobs.filter((_sj) => !subjobIds.includes(_sj.id)));
	}, [findAllAvailableSubjobs, setAvailableSubjobs, subjobs]);

	const tabsAdjustedForPermissions = React.useMemo(() => {
		const resolvedTabs: typeof TABS = [];
		for (const _tab of TABS) {
			if (_tab.id === TABS[2].id && !hasPermissionsToManageBillingCodes) {
				continue;
			}
			resolvedTabs.push(_tab);
		}
		return resolvedTabs;
	}, [hasPermissionsToManageBillingCodes]);

	const turnToProject = React.useCallback(() => {
		change('isProject', true);
		fetchAvailableSubjobs();
	}, [change, fetchAvailableSubjobs]);

	return (
		<>
			<Breadcrumbs
				items={[
					{ label: 'Jobs', url: CLIENT.COMPANY.JOBS.TABLE(orgAlias, companyName) },
					{ label: 'New Job' },
				]}
			/>
			<div className={styles['job-form__submit-section']}>
				<div className={styles['job-form__submit-section__hint']}>
					Please Fill out both Job and Job Hazard Assessment forms to submit.
				</div>
				<SubmitButton
					disabled={!isFormValid}
					label="Submit"
					onClick={handleSubmit(beforeCreateJob)}
					reduxFormSubmitting={submitting}
					variant="primary"
				/>
			</div>
			<TabNavigation
				active={activeTabId}
				navigationClassName={styles['job-form__tabs-navigation']}
				onClick={onTabSelect}
				renderLabel={renderTabLabel}
				tabs={tabsAdjustedForPermissions}
			/>
			<Outlet context={outletContext} />
			<ConfirmationModal
				body="Convert this Job to Project. This action is irreversible. Continue?"
				closeModal={closeTurnToProjectConfirmationModal}
				confirmAction={turnToProject}
				confirmText="Continue"
				modalStyle="grey-warning"
				showModal={showTurnToProjectConfirmationModal}
				title={'Convert Job to Project?'}
			/>
			<ConfirmationModal
				body={`Associate ${selectedSubjob?.jobCode} with ${formJobCode ?? 'Current Job'}. Continue?`}
				closeModal={closeAddSubjobConfirmationModal}
				confirmAction={addSubjob}
				confirmText="Continue"
				modalStyle="grey-warning"
				showModal={showAddSubjobConfirmationModal}
				title={`Associate ${selectedSubjob?.jobCode} with ${formJobCode ?? 'Current Job'}?`}
			/>
			<ConfirmationModal
				body={'You have associated Sub-jobs with this Project. After submiting the form Sub-jobs cannot be detached from the Project. Submit?'}
				closeModal={hideSubmitWithSubjobsConfirmationModal}
				confirmAction={handleSubmit(onSubmit)}
				confirmText="Submit"
				modalStyle="grey-warning"
				showModal={showSubmitWithSubjobsConfirmationModal}
				title={'Submit project with Sub-jobs?'}
			/>
		</>
	);
};

function mapStateToProps(state: RootState) {
	const { user: { companyData, userData } } = state;
	const formState = getFormValues(FORMS.JOB_CREATE);
	const formSelector = formValueSelector(FORMS.JOB_CREATE);
	const currentFormValues = getFormValues(FORMS.JOB_CREATE)(state) as JobUpsertFM;

	if (!userData || !companyData) {
		throw new Error('User not logged in');
	}

	const { isCompanyAdmin, permissions } = companyData;
	const { role } = userData;

	const hasPermissionsToManageBillingCodes = isAllowed(
		PagePermissions.COMPANY.JOBS.MANAGE_BILLING_CODES,
		permissions,
		isCompanyAdmin,
		role
	);

	const {
		jobCode,
		title,
		customerCompanyName,
		priority,
		office,
		jobHazardAssessmentStatus,
		isProject,
		subjobs,
		id: workRequestId,
		projectId,
		billToEmail,
		workLocation,
		invoiceReminderRecipients,
		uploadedAttachmentIds,
	} = formState(state) as JobUpsertFM ?? ({} as Partial<JobUpsertFM>);

	/** DB_DATE_ONLY "YYYY-MM-DD" */
	const dueDate: string = formSelector(state, 'startDate');

	const getSubmitErrors = getFormSubmitErrors(FORMS.JOB_CREATE);

	const { jobCode: jobCodeSubmitError, billingCodes: billingCodesSubmitErrors } = getSubmitErrors(state) as FormErrors<JobUpsertFM, string>;
	const syncErrors = getFormSyncErrors(FORMS.JOB_CREATE)(state) as FormErrorsWithArray<JobUpsertFM, string>;

	return {
		companyName: companyData.name,
		formJobCode: jobCode,
		title,
		customerCompanyName,
		priority,
		office,
		jobHazardAssessmentStatus,
		isProject,
		subjobs,
		workRequestId,
		projectId,
		dueDate,
		currentFormValues,
		billToEmail,
		jobCodeSubmitError,
		hasPermissionsToManageBillingCodes,
		syncErrors,
		billingCodesSubmitErrors: billingCodesSubmitErrors as unknown as FormErrorsWithArray<JobUpsertFM['billingCodes'][0], string>[],
		workLocation,
		invoiceReminderRecipients,
		uploadedAttachmentIds,
	};
}

function mapDispatchToProps() {
	return {
		createJob: JobActions.createJob,
		findAllAvailableSubjobs: WorkRequestActions.findAllAvailableSubjobs,
		deletePendingAttachments: AttachmentActions.deletePendingAttachments,
	};
}

const connector = connect(mapStateToProps, mapDispatchToProps());

const enhance = compose<React.ComponentClass>(
	reduxForm<JobUpsertFM, FormOwnProps>({
		form: FORMS.JOB_CREATE,
		validate: JobUpsertFM.validate,
		warn: JobUpsertFM.warn,
		shouldError: () => true, // show errors even when switching tabs
		initialValues: {
			priority: Priority.MEDIUM,
			estimateTotal: 0,
			numberOfEmployees: 0,
		},
	}),
	connector
);
export default enhance(CreateJob);
